'use client'

import { cn } from '@/lib/utils'
import { X } from 'lucide-react'
import { useState } from 'react'

interface CampaignSocialLinksFieldProps {
  label: string
  placeholder?: string
  value: readonly string[]
  onChange: (value: string[]) => void
  error?: { message?: string }
}

export const CampaignSocialLinksField = ({
  label,
  placeholder,
  value,
  onChange,
  error,
}: CampaignSocialLinksFieldProps) => {
  const [draft, setDraft] = useState('')

  const addLink = () => {
    const trimmed = draft.trim()
    if (!trimmed) {
      setDraft('')
      return
    }
    onChange([...value, trimmed])
    setDraft('')
  }

  const removeLink = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  return (
    <div className='flex w-full flex-col gap-1'>
      <p className='pl-3 text-sm text-neutral-500'>{label}</p>
      <div
        className={cn(
          'flex w-full flex-wrap items-center gap-2 rounded-2xl border border-neutral-300 bg-background px-4 py-2',
          error?.message && 'border-error-500'
        )}
      >
        {value.map((link, index) => (
          <span
            key={index}
            title={link}
            className='inline-flex h-6 max-w-[200px] items-center gap-1 rounded bg-neutral-100 px-2 text-sm text-neutral-800'
          >
            <span className='truncate'>{link}</span>
            <button
              type='button'
              onClick={() => removeLink(index)}
              aria-label='Remove'
              className='shrink-0 text-neutral-500 hover:text-neutral-800'
            >
              <X size={14} />
            </button>
          </span>
        ))}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault()
              addLink()
            }
          }}
          onBlur={addLink}
          placeholder={value.length === 0 ? placeholder : ''}
          className='h-6 min-w-[64px] flex-1 bg-transparent text-sm text-neutral-800 outline-none placeholder:text-neutral-400'
        />
      </div>
      {error?.message && (
        <p className='pl-3 text-xs text-error-500'>{error.message}</p>
      )}
    </div>
  )
}
